package fr.frux.core.maps

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.em
import androidx.compose.ui.unit.sp
import org.maplibre.compose.expressions.ast.Expression
import org.maplibre.compose.expressions.dsl.Feature
import org.maplibre.compose.expressions.dsl.asString
import org.maplibre.compose.expressions.dsl.case
import org.maplibre.compose.expressions.dsl.const
import org.maplibre.compose.expressions.dsl.image
import org.maplibre.compose.expressions.dsl.switch
import org.maplibre.compose.expressions.dsl.textOffset
import org.maplibre.compose.expressions.value.BooleanValue
import org.maplibre.compose.expressions.value.ImageValue
import org.maplibre.compose.expressions.value.StringValue
import org.maplibre.compose.layers.FeaturesClickHandler
import org.maplibre.compose.layers.SymbolLayer
import org.maplibre.compose.sources.VectorSource

/*
 * Apparences partagees par les regroupements du lot detaille et ceux de la pyramide (#622) : les
 * deux regimes doivent se confondre a l'ecran, d'ou un seul endroit ou les dessiner.
 */

/**
 * Sous ce seuil d'opacite, une couche en fondu laisse passer le toucher : pendant la bascule entre
 * les deux regimes, c'est le plus visible des deux qui repond.
 */
internal const val TAPPABLE_LAYER_OPACITY = 0.5f

/**
 * Icones composites des marqueurs (#269), une par cle de style, gardees d'un lot a l'autre. Le
 * meme [Painter] servant aux deux regimes, une meme combinaison n'est dessinee qu'une fois.
 */
internal class MarkerImageCache(
    private val markerIcons: Map<String, Painter>,
    private val fallbackGlyph: Painter?,
    /** Disque nu, repli des `switch` d'images : jamais atteint tant que les cles sont completes. */
    val disc: Painter,
    private val notchedDisc: Painter,
) {
    private val composites = mutableMapOf<String, Painter>()

    fun imagesFor(markers: List<MapMarker>): Map<String, Painter> =
        markers.distinctBy(::markerStyleKey).associate { marker ->
            val key = markerStyleKey(marker)
            key to
                composites.getOrPut(key) {
                    CompositeMarkerPainter(
                        notchedRim = if (marker.flagged) notchedDisc else null,
                        rimColor = markerRimColor(marker.soon),
                        plaqueColor = marker.tint,
                        // Meme repli que l'ancienne couche de glyphes : cle inconnue -> glyphe de
                        // secours, sinon la pastille reste nue.
                        glyph = marker.iconKey?.let { markerIcons[it] ?: fallbackGlyph },
                        glyphColor = GLYPH_COLOR,
                    )
                }
        }
}

@Composable
internal fun rememberMarkerImageCache(
    markerIcons: Map<String, Painter>,
    fallbackGlyph: Painter?,
): MarkerImageCache {
    val disc = rememberMarkerDiscPainter()
    val notchedDisc = rememberMarkerDiscNotchedPainter()
    return remember(markerIcons, fallbackGlyph, disc, notchedDisc) {
        MarkerImageCache(markerIcons, fallbackGlyph, disc, notchedDisc)
    }
}

/** Image de chaque feature, choisie par sa cle de style parmi [keys], dessinee au cote [side]. */
internal fun markerImageBySize(
    images: Map<String, Painter>,
    keys: Collection<String>,
    side: Dp,
    fallback: Painter,
): Expression<ImageValue> =
    switch(
        input = Feature[MARKER_STYLE_PROPERTY].asString(const(DEFAULT_STYLE_KEY)),
        *keys.map { key -> case(key, image(images.getValue(key), size = DpSize(side, side))) }.toTypedArray(),
        // Jamais atteint : les cas couvrent par construction toutes les combinaisons presentes.
        // Disque noir en garde-fou, visible si ce contrat casse.
        fallback = image(fallback, size = DpSize(side, side)),
    )

/** Pastille de regroupement, au meme bord blanc que les marqueurs. */
@Composable
internal fun rememberClusterDiscPainter(): Painter =
    remember {
        CompositeMarkerPainter(
            notchedRim = null,
            rimColor = MARKER_RIM_COLOR,
            plaqueColor = CLUSTER_COLOR,
            glyph = null,
            glyphColor = GLYPH_COLOR,
            rimScale = CLUSTER_SIZE / (CLUSTER_SIZE - CLUSTER_RIM_WIDTH * 2),
        )
    }

/** Satellite d'un badge (#710), plus petit et a glyphe proportionnellement plus gros. */
internal fun satellitePainter(
    badge: MarkerBadge,
    markerIcons: Map<String, Painter>,
    fallbackGlyph: Painter?,
): Painter =
    CompositeMarkerPainter(
        notchedRim = null,
        rimColor = MARKER_RIM_COLOR,
        plaqueColor = badge.tint,
        glyph = markerIcons[badge.iconKey] ?: fallbackGlyph,
        glyphColor = GLYPH_COLOR,
        rimScale = SATELLITE_SIZE / (SATELLITE_SIZE - SATELLITE_RIM_WIDTH * 2),
        glyphFraction = SATELLITE_GLYPH_FRACTION,
    )

/**
 * Pastille et compteur d'un seul symbole, et non un cercle sous un texte (#710) : pendant un zoom,
 * MapLibre retire les cercles d'une tuile perimee d'un coup mais estompe ses symboles en fondu. La
 * couronne de satellites survivait alors un instant a sa pastille. En symbole, tout le regroupement
 * s'estompe ensemble.
 */
@Composable
internal fun ClusterDiscLayer(
    id: String,
    source: VectorSource,
    filter: Expression<BooleanValue>,
    countText: Expression<StringValue>,
    disc: Painter,
    opacity: Float,
    onClick: FeaturesClickHandler,
) {
    SymbolLayer(
        id = id,
        source = source,
        filter = filter,
        iconImage = image(disc, size = DpSize(CLUSTER_SIZE, CLUSTER_SIZE)),
        iconOpacity = const(opacity),
        iconAllowOverlap = const(true),
        textField = countText,
        // Police du fond de carte : le serveur de glyphes de tiles.frux-app.fr ne sert que celles
        // du style, la police par défaut de MapLibre y rend 404 et la pastille s'affiche sans son
        // chiffre.
        textFont = const(listOf(CLUSTER_FONT)),
        textColor = const(CLUSTER_TEXT_COLOR),
        textSize = const(13.sp),
        textOffset = textOffset(0.em, CLUSTER_TEXT_BASELINE_SHIFT_EM.em),
        textOpacity = const(opacity),
        textAllowOverlap = const(true),
        onClick = onClick,
    )
}
